/**
 * @fileoverview Componente de avatar do usuário com fallback de iniciais.
 *
 * Suporta 5 tamanhos (xs a xl), exibição opcional de nome/email,
 * e fallback automático com iniciais quando não há imagem.
 * Usado em sidebar, top bar, membros de equipe e cards.
 */
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn, getInitials, avatarFallbackStyle } from '@/lib/utils';

const SIZE_CLASSES = {
  xs: 'h-5 w-5',
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
  xl: 'h-12 w-12',
} as const;

const TEXT_CLASSES = {
  xs: 'text-[8px]',
  sm: 'text-[9px]',
  md: 'text-[10px]',
  lg: 'text-xs',
  xl: 'text-sm',
} as const;

export type AvatarSize = keyof typeof SIZE_CLASSES;

export interface UserAvatarProps {
  name: string;
  avatarUrl?: string | null;
  size?: AvatarSize;
  showName?: boolean;
  email?: string;
  className?: string;
}

/** Shared Avatar component with initials fallback and optional name+email layout. */
export function UserAvatar({
  name,
  avatarUrl,
  size = 'md',
  showName = false,
  email,
  className,
}: UserAvatarProps) {
  const initials = getInitials(name);
  // Per-name hue for the fallback. Computed once, used across both the
  // standalone and the showName layouts. font-bold added so the initials
  // pop against the tinted background.
  const fallbackStyle = avatarFallbackStyle(name);
  const fallbackClass = cn(TEXT_CLASSES[size], 'font-bold');

  const avatar = (
    <Avatar className={SIZE_CLASSES[size]}>
      <AvatarImage src={avatarUrl ?? ''} />
      <AvatarFallback className={fallbackClass} style={fallbackStyle}>{initials}</AvatarFallback>
    </Avatar>
  );

  if (showName) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        {avatar}
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium truncate leading-tight">{name}</span>
          {email && (
            <span className="text-xs text-muted-foreground truncate leading-tight">{email}</span>
          )}
        </div>
      </div>
    );
  }

  return <Avatar className={cn(SIZE_CLASSES[size], className)}>
    <AvatarImage src={avatarUrl ?? ''} />
    <AvatarFallback className={fallbackClass} style={fallbackStyle}>{initials}</AvatarFallback>
  </Avatar>;
}
